import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { Account } from "@vixi/types";
import { LangKey } from "@vixi/localization";
import { authRequestInterceptor, authResponseInterceptor } from "./interceptors/authInterceptor";
import api_urls from "./api_urls";
import { refreshAccessToken } from "./utils";
import { logger, type Logger } from "@vixi/utils";
import { endpoints, EndpointValues } from "./endpoints";

export interface ApiConfig {
  redirectToLogin?: () => void;
}

export class Api<Endpoints extends Record<string, string | ((...args: any[]) => string)>> {
  private axiosInstance: AxiosInstance;

  logger: Logger = logger;
  endpoints: Endpoints;
  private redirectToLogin?: ApiConfig["redirectToLogin"];

  constructor(url: string, endpoints: Endpoints, _config?: ApiConfig) {
    this.axiosInstance = axios.create({
      baseURL: url,
      validateStatus: (status) => status < 400 || status === 401,
    });

    this.axiosInstance.interceptors.request.use(authRequestInterceptor);
    this.axiosInstance.interceptors.response.use(authResponseInterceptor);

    this.endpoints = endpoints;
  }

  async catchError<T>(error: AxiosError<T>): Promise<AxiosResponse<T>> {
    if (error.code === "ERR_CANCELED") throw error;

    // Only handle 401 unauthorized errors
    // Don't handle CORS errors or 500 errors - they should be handled by the backend
    if (error.response?.status === 401) {
      try {
        return await this.catchUnauthorizedError(error);
      } catch (unauthorizedErr) {
        // If unauthorized error handling fails, throw original error
        throw error;
      }
    }

    // For all other errors (500, CORS, etc.), throw the original error
    throw error;
  }

  async catchUnauthorizedError<T>(error: AxiosError<T>): Promise<AxiosResponse<T>> {
    if (error.request?.status !== 401) throw error;

    try {
      const tokenData = await refreshAccessToken();
      this.logger?.debug("Retrying request after token refresh");
      if (!error.config || !tokenData) throw error;

      error.config.headers.setAuthorization(`Bearer ${tokenData.accessToken}`);

      const res = await axios.request<T>(error.config);

      return res;
    } catch (error2) {
      if ((error2 as AxiosError).code === "ERR_CANCELED") throw error;

      this.redirectToLogin?.();
      throw error;
    }
  }

  async catchCorsError<T>(error: AxiosError<T>): Promise<AxiosResponse<T>> {
    // Only handle actual CORS errors (status 0, no response, online)
    // Don't make getUserInfo call as it causes unnecessary 401 errors
    if (error.request?.status === 0 && !error.response && navigator.onLine) {
      // If it's a CORS error, just throw it - don't try to retry
      // CORS errors usually mean the server is not configured correctly
      // or we're in production build without proxy
      this.logger?.error("CORS error occurred. Check if you're using production build without proxy.");
      throw new AxiosError(
        "ErrorMessage.SomethingWentWrong" satisfies LangKey,
        "ERR_CORS",
        error.config,
      );
    }

    throw error;
  }

  async get<TData>(endpoint: EndpointValues<Endpoints>, config?: AxiosRequestConfig<TData>) {
    try {
      return await this.axiosInstance.get<TData>(endpoint as string, config);
    } catch (error) {
      if (!(error instanceof AxiosError)) throw error;
      return this.catchError(error as AxiosError<TData>);
    }
  }

  async post<TData = any>(
    endpoint: EndpointValues<Endpoints>,
    data?: any,
    config?: AxiosRequestConfig<TData>,
  ) {
    try {
      return await this.axiosInstance.post<TData>(endpoint as string, data, {
        ...config,
      });
    } catch (error) {
      if (!(error instanceof AxiosError)) throw error;
      return this.catchError(error as AxiosError<TData>);
    }
  }

  async put<TData>(
    endpoint: EndpointValues<Endpoints>,
    data: any,
    config?: AxiosRequestConfig<TData>,
  ) {
    try {
      return await this.axiosInstance.put<TData>(endpoint as string, data, {
        ...config,
      });
    } catch (error) {
      if (!(error instanceof AxiosError)) throw error;
      return this.catchError(error as AxiosError<TData>);
    }
  }

  async delete<TData>(endpoint: EndpointValues<Endpoints>, config?: AxiosRequestConfig<TData>) {
    try {
      return await this.axiosInstance.delete<TData>(endpoint as string, {
        ...config,
      });
    } catch (error) {
      if (!(error instanceof AxiosError)) throw error;
      return this.catchError(error as AxiosError<TData>);
    }
  }

  getDefaultConfig() {
    return this.axiosInstance.defaults;
  }

  getConfig<TData>(
    method: Method,
    endpoint: EndpointValues<Endpoints>,
    config?: AxiosRequestConfig<TData>,
  ): AxiosRequestConfig<TData> {
    const defaultConfig = this.axiosInstance.defaults;
    return {
      method,
      ...defaultConfig,
      ...config,
      url: endpoint as string,
      headers: {
        ...defaultConfig.headers.common,
        ...config?.headers,
      },
    };
  }
}

export const api = new Api(api_urls.coursemaker, endpoints);
